/**
 * Single-tag ESM loader. Install:
 *   <script type="module" src="https://<api>/widget/v1/widget.es.js?w=wk_..."></script>
 *
 * Reads the widget key from its own ?w= and derives api-base from the bundle's
 * origin, then injects <kobot-chat-widget>. The host pastes ONE tag — this is
 * the only supported install (no UMD / two-part embed). If an element is already
 * present (e.g. the same script tag included twice), we don't inject another.
 *
 * import.meta.url is only valid in ES modules — this bundle is ES-only.
 */
import './index.js'; // registers the custom element + sets window.<Brand>Widget
import { WIDGET_TAG, BRAND_NAME } from './brand.js';

export function parseWidgetKey(href: string): string | null {
  try {
    return new URL(href).searchParams.get('w');
  } catch {
    return null;
  }
}

export function deriveApiBase(href: string): string {
  return `${new URL(href).origin}/api/v1/widget`;
}

export function injectWidget(
  doc: Document,
  attrs: { key: string | null; apiBase: string },
): void {
  if (!attrs.key) {
    console.warn(`[${BRAND_NAME}] loader: missing ?w= widget key — nothing injected`);
    return;
  }
  if (doc.querySelector(WIDGET_TAG)) return; // already injected (script tag present twice)
  const el = doc.createElement(WIDGET_TAG);
  el.setAttribute('widget-key', attrs.key);
  el.setAttribute('api-base', attrs.apiBase);
  (doc.body ?? doc.documentElement).appendChild(el);
}

// --- bootstrap (runs on import in the served ESM bundle) ----------------
if (typeof document !== 'undefined') {
  try {
    const href = import.meta.url;
    injectWidget(document, { key: parseWidgetKey(href), apiBase: deriveApiBase(href) });
  } catch {
    // Malformed import.meta.url in unusual environments — skip bootstrap silently
  }
}
